import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { BlastStatus } from '../../lib';
import {
    BLAST_AI_CONVERSATION,
    ChatMessage,
    MessageType,
} from '../../pages/ask-blast/ask-blast-helper';

interface BlastAIContextProviderProps {
    children: ReactNode;
}

interface BlastAIContextType {
    blastConversation: BlastAIChat;
    status: BlastStatus;
    isLoading: boolean;
    handleNewUserMessage: (
        message: string,
        userName: string,
        sdzName: string
    ) => void;
    handleNewSystemMessage: (
        newConversationId: string | undefined,
        message: string,
        type: MessageType
    ) => void;
    clearConversation: () => void;
}

interface BlastAIChat {
    conversationId?: string;
    conversation: ChatMessage[];
    sdzName: string;
    userName: string;
    status: BlastStatus;
}

const defaultBlastAIChat: BlastAIChat = {
    conversationId: undefined,
    conversation: [],
    sdzName: '',
    userName: '',
    status: 'new',
};

export const BlastAIContext = createContext<BlastAIContextType | undefined>(
    undefined
);

const BlastAIContextProvider = ({
    children,
}: BlastAIContextProviderProps): React.JSX.Element => {
    const [blastConversation, setBlastConversation] =
        useState<BlastAIChat>(defaultBlastAIChat);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [status, setStatus] = useState<BlastStatus>('new');

    const setLastSavedMessageStatus = (messages: ChatMessage[]): void => {
        const getLastMessageType: string =
            messages[messages.length - 1]?.type ?? 'user';
        setStatus(getLastMessageType === 'error' ? 'error' : 'success');
    };

    const setLastSavedConversation = (chat: BlastAIChat): void => {
        setBlastConversation(chat);

        const messages: ChatMessage[] = chat.conversation;
        setLastSavedMessageStatus(messages);
    };

    useEffect(() => {
        // Try load current conversation on load if there's anything in localStorage.
        const savedConversation: string | null = localStorage.getItem(
            BLAST_AI_CONVERSATION
        );

        if (savedConversation) {
            setLastSavedConversation(JSON.parse(savedConversation));
        }

        window.addEventListener('storage', (event: any) => {
            // Listen to the blastAIConversation in localStorage. Update current conversation on localStorage change.
            // This will trigger/update in case user updates current conversation in a new tab.
            if (event.key === BLAST_AI_CONVERSATION) {
                const blastAIChat: BlastAIChat = event.newValue
                    ? JSON.parse(event.newValue)
                    : defaultBlastAIChat;
                setLastSavedConversation(blastAIChat);
            }
        });
    }, []);

    useEffect(() => {
        // Only update conversation in localStorage when there's a valid change. The default value means
        // user either hasn't established a new conversation or deleted the conversation manually.
        if (
            JSON.stringify(blastConversation) !==
            JSON.stringify(defaultBlastAIChat)
        ) {
            localStorage.setItem(
                BLAST_AI_CONVERSATION,
                JSON.stringify(blastConversation)
            );
        }
    }, [blastConversation, isLoading]);

    const updateBlastConversationId = (id: string | undefined): void => {
        setBlastConversation((prevConversation) => ({
            ...prevConversation,
            conversationId: id,
        }));
    };

    const addPendingMessage = (): void => {
        const newPendingMessage: ChatMessage = {
            type: 'pending',
            value: 'Generating Answer...',
        };
        setBlastConversation((prevConversation) => ({
            ...prevConversation,
            conversation: [...prevConversation.conversation, newPendingMessage],
        }));
    };

    const removePendingMessage = (): void => {
        setBlastConversation((prevConversation) => ({
            ...prevConversation,
            conversation: prevConversation.conversation.filter(
                (message) => message.type !== 'pending'
            ),
        }));
    };

    const addUserMessage = (
        userPrompt: string,
        userName: string,
        sdzName: string
    ): void => {
        const newUserMessage: ChatMessage = {
            type: 'user',
            title: 'Question',
            value: userPrompt,
        };
        setBlastConversation((prevConversation) => ({
            ...prevConversation,
            conversation: [...prevConversation.conversation, newUserMessage],
            userName,
            sdzName,
        }));
    };

    const addSystemMessage = (
        message: string,
        messageType: MessageType
    ): void => {
        const newBlastMessage: ChatMessage = {
            type: messageType,
            title: 'Answer',
            value: message,
        };
        setBlastConversation((prevConversation) => ({
            ...prevConversation,
            conversation: [...prevConversation.conversation, newBlastMessage],
        }));
    };

    const handleNewUserMessage = (
        userPrompt: string,
        userName: string,
        sdzName: string
    ): void => {
        addUserMessage(userPrompt, userName, sdzName);
        addPendingMessage();
        setIsLoading(true);
        setStatus('loading');
    };

    const handleNewSystemMessage = (
        newConversationId: string | undefined,
        message: string,
        type: MessageType
    ): void => {
        updateBlastConversationId(newConversationId);
        removePendingMessage();
        setIsLoading(false);
        setStatus(type === 'error' ? 'error' : 'success');
        addSystemMessage(message, type);
    };

    const clearConversation = (): void => {
        setStatus('new');
        setBlastConversation(defaultBlastAIChat);
        localStorage.removeItem(BLAST_AI_CONVERSATION);
    };

    const value = useMemo(
        () => ({
            blastConversation,
            status,
            isLoading,
            handleNewUserMessage,
            handleNewSystemMessage,
            clearConversation,
        }),
        [blastConversation, isLoading] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <BlastAIContext.Provider value={value}>
            {children}
        </BlastAIContext.Provider>
    );
};

const useBlastAI = (): BlastAIContextType => {
    const context = useContext(BlastAIContext);
    if (!context) {
        throw new Error('useBlastAI must be used within an BlastAIProvider');
    }
    return context;
};

export { BlastAIContextProvider, useBlastAI };
